/**
 * Casio fx-991EX — Newton's Method SOLVE
 * QSG p.5–6: SHIFT+CALC → SOLVE
 *
 * Solves f(x) = 0 by Newton–Raphson iteration.
 * The user enters an equation like "x^3 + x - 100" (implicitly = 0),
 * or "x^3 + x = 100" (split at =).
 */
import { evalWithAngle, ANGLE } from './evaluator';

const MAX_ITER = 200;
const TOL = 1e-12;
const H = 1e-8;

/**
 * Solve exprStr = 0 for the given variable.
 * @param {string} exprStr  math.js expression (one side, or "lhs - (rhs)" if = was present)
 * @param {string} varName  variable to solve for (e.g. 'x')
 * @param {number} guess    initial guess
 * @param {string} angleMode  'DEG'|'RAD'|'GRAD'
 * @param {object} vars     other variable values { Ans, M, A, ... }
 * @returns {{ solution: number, lMinusR: number, iterations: number }}
 */
export function solve(exprStr, varName, guess, angleMode = ANGLE.DEG, vars = {}) {
  let x = guess;

  const f = (val) => {
    const scope = { ...vars, [varName]: val };
    return evalWithAngle(exprStr, angleMode, scope);
  };

  for (let i = 0; i < MAX_ITER; i++) {
    const fx = f(x);
    if (Math.abs(fx) < TOL) {
      return { solution: x, lMinusR: fx, iterations: i + 1 };
    }

    // Numerical derivative (central difference)
    const dfx = (f(x + H) - f(x - H)) / (2 * H);

    if (Math.abs(dfx) < 1e-20) {
      // Derivative too small — try bisection-like step
      x = x + (Math.random() - 0.5) * 0.1;
      continue;
    }

    const step = fx / dfx;
    x = x - step;

    // Guard against divergence
    if (!isFinite(x) || isNaN(x)) {
      throw new Error("Can't Solve");
    }
  }

  // Didn't converge to TOL, but return best estimate
  const finalFx = f(x);
  if (Math.abs(finalFx) < 1e-4) {
    return { solution: x, lMinusR: finalFx, iterations: MAX_ITER };
  }
  throw new Error("Can't Solve");
}

/**
 * Parse an equation string that may contain '='.
 * "x^3 + x = 100" → "((x^3 + x) - (100))"
 * "x^3 + x - 100" → "x^3 + x - 100"
 */
export function parseEquation(exprStr) {
  const eqIdx = exprStr.indexOf('=');
  if (eqIdx === -1) return exprStr;
  const lhs = exprStr.substring(0, eqIdx).trim();
  const rhs = exprStr.substring(eqIdx + 1).trim();
  return `((${lhs}) - (${rhs}))`;
}

/**
 * Detect which variables appear in an expression (simple scan).
 */
export function findVariables(exprStr) {
  const found = [];
  const candidates = ['x', 'y', 'A', 'B', 'C', 'D', 'E', 'F'];
  for (const v of candidates) {
    // Match standalone variable (not part of function name)
    const re = new RegExp(`(?<![a-zA-Z])${v}(?![a-zA-Z(])`, 'g');
    if (re.test(exprStr)) found.push(v);
  }
  return found;
}
